import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { kinds, type EnvKey } from '@/app/site/scope';
import type { ShopSettingsBody } from '@/shared/api/generated/model';
import { shopReviewsCreate } from '@/shared/api/generated/shop/shop';
import { ApiError } from '@/shared/api/http';
import { Button } from '@/shared/ui/Button';
import { toast } from '@/shared/ui/Toast';

import styles from './ShopPage.module.scss';

/**
 * Рейтинг товаров (шаг 9.12): из какой коллекции отзывов он считается.
 * «Завести отзывы» собирает коллекцию из деталей ядра — модерация, вход,
 * один отзыв на товар от посетителя.
 */
export function ReviewsBlock({
  siteId,
  env,
  shop,
  onChanged,
}: {
  siteId: string;
  env: EnvKey;
  shop: ShopSettingsBody;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const reviews = shop.reviews?.type;

  const create = () => {
    setBusy(true);

    shopReviewsCreate(siteId, kinds[env])
      .then(() => {
        toast.success('Отзывы о товарах заведены');
        onChanged();
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof ApiError ? cause.detail : 'Не удалось завести отзывы');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <>
      <h3 className={styles['panel__title']}>Рейтинг товаров</h3>
      {reviews === undefined ? (
        <>
          <p className={styles['muted']}>
            Средняя оценка и число отзывов у товара — из коллекции отзывов. Заведётся «Отзывы о
            товарах»: оставляют вошедшие, один отзыв на товар, команда модерирует; в рейтинге —
            одобренные и опубликованные.
          </p>
          <div>
            <Button size="sm" variant="outline" loading={busy} onClick={create}>
              Завести отзывы о товарах
            </Button>
          </div>
        </>
      ) : (
        <p className={styles['muted']}>
          Из{' '}
          <Link
            to="/sites/$siteId/$env/models/$typeId"
            params={{ siteId, env, typeId: reviews.id }}
          >
            «{reviews.name}»
          </Link>
          {': оценка — поле '}
          <span className={styles['mono']}>{shop.reviews?.rating_field}</span>
          {shop.reviews?.approved_stage
            ? `, только в статусе «${shop.reviews.approved_stage}» и опубликованные.`
            : ', опубликованные.'}
        </p>
      )}
    </>
  );
}
